import { useState } from 'react';

import {
  Alert,
  ArrowDown,
  ArrowUp,
  Badge,
  ButtonV2 as Button,
  ChakraBox,
  ChakraButton,
  ChakraCollapse,
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  ChakraText,
  ChatUser,
  DateFormat,
  Divider,
  Edit,
  EquipUIFireflyV2Theme,
  EquipUITheme,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import ConfirmModal from '~/components/common/ConfirmModal';
import ErrorMessage from '~/components/common/ErrorMessage';
import BaseTitleAndValue, {
  TitleAndValueProps,
} from '~/components/common/TitleAndValue';
import ChannelMembers from '~/components/maud/patients/profile/common/ChannelMembers';
import { GOAL_STATUS } from '~/lib/constants/goals.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { getAssigneeNamesFromIds } from '~/lib/util/goals.util';
import { getPreferredFullName } from '~/lib/util/patient.util';
import { useGetAppUsersQuery } from '~/store/services/appUsers';
import { useVoidGoalMutation } from '~/store/services/clinicalData';

import EditWeightGoalItem from './EditWeightGoalItem';
import WeightGoalItemActivity from './WeightGoalItemActivity';

const { neutral, primary, red, critical, background } =
  EquipUIFireflyV2Theme.colors;
const { h6, label, captionBold, caption, labelBold } = EquipUITheme.typography;

type WeightGoalsListItemProps = {
  goal: PatientWeightGoalType;
  goalsList: PatientWeightGoalType[];
  patientId: string;
};

const statusBadgeStyleMapper: Record<
  GOAL_STATUS,
  Record<string, string | Record<'true' | 'false', string>>
> = {
  [GOAL_STATUS.ACTIVE]: {
    badgeColor: primary[10],
    fontColor: background.button['primary.active'],
  },
  [GOAL_STATUS.COMPLETE]: {
    badgeColor: neutral.background.secondary,
    fontColor: neutral.secondary,
  },
  [GOAL_STATUS.VOID]: {
    badgeColor: red[10],
    fontColor: red.shade,
  },
};

const TitleAndValue = ({ ...props }: TitleAndValueProps) => (
  <BaseTitleAndValue
    {...props}
    fontSize="14px"
    marginBottom="24px"
    marginTop="16px"
    titleColor="neutral.primary"
  />
);

export const WeightGoalsListItem = ({
  goal,
  goalsList,
  patientId,
}: WeightGoalsListItemProps) => {
  const {
    goalEndDate,
    goalAssigneeIds,
    goalFrequency,
    goalFrequencyReason,
    goalMethod,
    goalStatus,
    goalType,
    highBound,
    lowBound,
    observationGoalExternalId,
    goalStartDate,
  } = goal;

  const TOP_LEVEL_ID = `goal-item-${observationGoalExternalId}`;
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);
  const { currentPatientId } = useGlobalContext();
  const { data: taskAssignees = [] } = useGetAppUsersQuery(
    { includeGeneral: true, includeInactive: true, patientId },
    {
      skip: !currentPatientId,
    },
  );

  const assigneeNames = getAssigneeNamesFromIds(goalAssigneeIds, taskAssignees);

  const ToggleIcon = isExpanded ? ArrowUp : ArrowDown;

  const startDateValue = goalStartDate
    ? DateTime.fromISO(goalStartDate).toFormat(DateFormat.longDate)
    : '-';

  const endDateValue = goalEndDate
    ? DateTime.fromISO(goalEndDate).toFormat(DateFormat.longDate)
    : '-';

  const [voidGoal, { isLoading: voidGoalLoading, error: voidGoalError }] =
    useVoidGoalMutation();

  const onVoidConfirm = () => {
    voidGoal({
      patientId,
      observationGoalExternalId,
    }).then(() => {
      setIsVoidModalOpen(false);
    });
  };

  const selectedAssignees: ChatUser[] = goalAssigneeIds
    ?.reduce((assignedAppUsers, eachId) => {
      const assignedAppUser = taskAssignees?.find(
        ({ externalId }) => externalId === eachId,
      );
      if (assignedAppUser) {
        assignedAppUsers.push(assignedAppUser);
      }
      return assignedAppUsers;
    }, [])
    ?.map(
      ({ chosenName, externalId, firstName, lastName, relationToPatient }) => {
        return {
          metaData: { providerType: relationToPatient },
          nickname: getPreferredFullName(chosenName, firstName, lastName),
          userId: externalId,
        };
      },
    );

  let boundaryText = '-';
  if (lowBound && highBound) {
    boundaryText = `${lowBound} lbs to ${highBound} lbs`;
  } else if (lowBound) {
    boundaryText = ` ${lowBound} lbs`;
  }

  return (
    <ChakraSimpleGrid
      borderColor="neutral.line"
      borderRadius="16px"
      borderStyle="solid"
      borderWidth="2px"
      gap={2}
      id={`${TOP_LEVEL_ID}__container`}
      marginY="5px"
      padding="12px 16px"
    >
      <ConfirmModal
        body="Are you sure you want to void this goal? This action cannot be undone."
        cancelLabel="Cancel"
        confirmLabel="Void goal"
        confirmVariant="accent.critical"
        isOpen={isVoidModalOpen}
        onClose={() => setIsVoidModalOpen(false)}
        onConfirm={onVoidConfirm}
        title="Void goal"
      />

      <ChakraGridItem>
        <ChakraFlex alignItems="center" justifyContent="space-between">
          <ChakraBox>
            <ChakraText
              {...h6}
              data-testid="goal-item-title"
              id={`${TOP_LEVEL_ID}__title`}
            >
              {goalType ?? '-'}
            </ChakraText>

            <ChakraText
              {...label}
              color="neutral.secondary"
              data-testid="goal-item-duration"
              id={`${TOP_LEVEL_ID}__duration-text`}
            >
              {`From ${startDateValue} to ${endDateValue}`}
            </ChakraText>
          </ChakraBox>

          <ChakraFlex id={`${TOP_LEVEL_ID}__boundary-container`}>
            <ChakraText {...captionBold} id={`${TOP_LEVEL_ID}__boundary-title`}>
              Boundary
            </ChakraText>
            <ChakraText
              {...caption}
              data-testid="goal-item-boundary"
              id={`${TOP_LEVEL_ID}__boundary-size`}
              marginLeft="8px"
            >
              {boundaryText}
            </ChakraText>
          </ChakraFlex>

          <ChakraFlex
            alignItems="center"
            id={`${TOP_LEVEL_ID}__assignees-container`}
          >
            <ChakraText
              {...captionBold}
              id={`${TOP_LEVEL_ID}__assigned-to-title`}
            >
              Assigned to
            </ChakraText>

            {selectedAssignees?.length > 0 && (
              <ChannelMembers
                id={`${TOP_LEVEL_ID}__user-avatars`}
                key={`${TOP_LEVEL_ID}__user-avatars`}
                members={selectedAssignees}
                paddedTop={false}
                showPopoverOnHover
              />
            )}
          </ChakraFlex>

          <ChakraFlex alignItems="center">
            <Badge
              {...captionBold}
              badgeColor={statusBadgeStyleMapper[goalStatus].badgeColor}
              fontColor={statusBadgeStyleMapper[goalStatus].fontColor}
              id={`${TOP_LEVEL_ID}__status-badge`}
              margin="0"
            >
              {goalStatus}
            </Badge>
            <Button
              cursor="pointer"
              id={`${TOP_LEVEL_ID}__toggle`}
              onClick={() => setIsExpanded(!isExpanded)}
              rightIcon={
                <ToggleIcon fill={primary[100]} height="12px" width="12px" />
              }
              size="xs"
              variant="link"
            />
          </ChakraFlex>
        </ChakraFlex>
      </ChakraGridItem>

      <ChakraCollapse in={isExpanded}>
        <Divider id={`${TOP_LEVEL_ID}__goal-details`} marginBottom="24px" />
        {!isEditing && (
          <ChakraBox padding="0px 8px">
            <ChakraSimpleGrid columns={4} spacing={10}>
              <ChakraGridItem>
                <TitleAndValue title="Type" value={goalType ?? '-'} />
                <TitleAndValue title="Start date" value={startDateValue} />
                {goalFrequencyReason && (
                  <TitleAndValue
                    title="Alternate frequency reason"
                    value={goalFrequencyReason}
                  />
                )}
              </ChakraGridItem>
              <ChakraGridItem>
                <TitleAndValue title="Frequency" value={goalFrequency ?? '-'} />
                <TitleAndValue title="End date" value={endDateValue} />
              </ChakraGridItem>
              <ChakraGridItem>
                <TitleAndValue title="Method" value={goalMethod ?? '-'} />
                <TitleAndValue
                  title="Low boundary"
                  value={lowBound ? `${lowBound} lbs` : '-'}
                />
              </ChakraGridItem>
              <ChakraGridItem>
                <TitleAndValue
                  title="Assignees"
                  value={
                    assigneeNames?.length > 0 ? assigneeNames.join(', ') : '-'
                  }
                />
                <TitleAndValue
                  title="High boundary"
                  value={highBound ? `${highBound} lbs` : '-'}
                />
              </ChakraGridItem>
            </ChakraSimpleGrid>

            <ChakraFlex alignItems="center" justifyContent="space-between">
              <ChakraFlex>
                <ChakraButton
                  {...labelBold}
                  color="critical.base"
                  id={`${TOP_LEVEL_ID}__void}`}
                  isDisabled={isEditing}
                  isLoading={voidGoalLoading}
                  onClick={() => {
                    setIsVoidModalOpen(true);
                  }}
                  rightIcon={
                    <Alert fill={critical.base} height="12px" width="12px" />
                  }
                  size="xs"
                  variant="link"
                >
                  Void goal
                </ChakraButton>
                {voidGoalError && (
                  <ChakraBox paddingBottom="18px">
                    <ErrorMessage id={`${TOP_LEVEL_ID}__void-error`}>
                      Error voiding goal
                    </ErrorMessage>
                  </ChakraBox>
                )}
              </ChakraFlex>
              <Button
                {...labelBold}
                id={`${TOP_LEVEL_ID}__edit`}
                onClick={() => setIsEditing(true)}
                rightIcon={
                  <Edit fill={primary[100]} height="12px" width="12px" />
                }
                size="xs"
                variant="link"
              >
                Edit
              </Button>
            </ChakraFlex>
          </ChakraBox>
        )}

        {isEditing && (
          <EditWeightGoalItem
            goalData={goal}
            goalsList={goalsList}
            onCancel={() => setIsEditing(false)}
            onSave={() => {
              setIsEditing(false);
            }}
            patientId={patientId}
          />
        )}
        <WeightGoalItemActivity
          goalId={observationGoalExternalId}
          patientId={patientId}
        />
      </ChakraCollapse>
    </ChakraSimpleGrid>
  );
};
